import { useMemo, useState, useEffect } from 'react';
import type { DragEvent, KeyboardEvent as ReactKeyboardEvent, MouseEvent } from 'react';
import { r2BucketList } from '../../api/r2/bucketList';
import { r2BucketTree, type R2BucketTreeItem } from '../../api/r2/bucketTree';
import { r2DirectoryCreate } from '../../api/r2/directoryCreate';
import { r2FileBulkContent } from '../../api/r2/fileBulkContent';
import { r2FileBulkTransfer } from '../../api/r2/fileBulkTransfer';
import { r2FileCreate } from '../../api/r2/fileCreate';
import { r2FileDelete } from '../../api/r2/fileDelete';
import { r2FileMove } from '../../api/r2/fileMove';
import { r2FileTransfer } from '../../api/r2/fileTransfer';
import { r2ReferenceResolve } from '../../api/r2/referenceResolve';
import { vscodeTheme } from '../../styles/theme';
import { ContextMenu, ContextMenuItem } from '../shared/ContextMenu';
import { SearchableSelect } from '../shared/SearchableSelect';
import { useToast } from '../shared/Toast';
import {
  blobToDataUrl,
  captureElementToPngBlob,
  copyBlobToClipboard,
  downloadBlob,
} from '../../utils/screenshot';

interface TreeNode {
  name: string;
  path: string;
  isFolder: boolean;
  size?: number;
  children?: TreeNode[];
}

interface FileTreeProps {
  bucketId: string;
  onFileSelect: (key: string) => void;
}

const normalizeDirectoryPath = (path: string) =>
  path.endsWith('/') ? path : `${path}/`;

const getBaseName = (path: string) => {
  const trimmed = path.endsWith('/') ? path.slice(0, -1) : path;
  const parts = trimmed.split('/').filter(Boolean);
  return parts[parts.length - 1] ?? '';
};

const getParentDirectory = (path: string) => {
  const trimmed = path.endsWith('/') ? path.slice(0, -1) : path;
  const parts = trimmed.split('/').filter(Boolean);
  parts.pop();
  return parts.length ? `${parts.join('/')}/` : '';
};

const isFilePath = (path: string) => !path.endsWith('/');

const splitNameAndExtension = (name: string) => {
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex > 0) {
    return { base: name.slice(0, dotIndex), ext: name.slice(dotIndex) };
  }
  return { base: name, ext: '' };
};

const buildUniqueName = (name: string, suffixIndex: number) => {
  const { base, ext } = splitNameAndExtension(name);
  return `${base}_${suffixIndex}${ext}`;
};

const getDirectChildFileNames = (
  items: R2BucketTreeItem[],
  destinationDir: string
) => {
  const normalizedDestination = destinationDir
    ? normalizeDirectoryPath(destinationDir)
    : '';
  const names = new Set<string>();
  for (const item of items) {
    if (item.type !== 'file') {
      continue;
    }
    const filePath = item.path;
    if (normalizedDestination) {
      if (!filePath.startsWith(normalizedDestination)) {
        continue;
      }
      const remainder = filePath.slice(normalizedDestination.length);
      if (!remainder || remainder.includes('/')) {
        continue;
      }
      names.add(remainder);
    } else {
      const trimmed = filePath.replace(/\/$/, '');
      if (!trimmed.includes('/')) {
        names.add(trimmed);
      }
    }
  }
  return names;
};

const sortTreeNodes = (nodes: TreeNode[]) => {
  nodes.sort((a, b) => {
    if (a.isFolder && !b.isFolder) return -1;
    if (!a.isFolder && b.isFolder) return 1;
    return a.name.localeCompare(b.name);
  });
  for (const node of nodes) {
    if (node.children) {
      sortTreeNodes(node.children);
    }
  }
};

const buildTreeFromFlatList = (items: Array<{
  path: string;
  type: 'file' | 'directory';
  size?: number;
}>) => {
  const root: TreeNode[] = [];
  const nodeMap = new Map<string, TreeNode>();

  const ensureFolder = (folderPath: string) => {
    const normalized = normalizeDirectoryPath(folderPath);
    const trimmed = normalized.replace(/\/$/, '');
    if (!trimmed) {
      return null;
    }
    const parts = trimmed.split('/').filter(Boolean);
    let currentPath = '';
    let parent = root;
    let currentNode: TreeNode | null = null;

    for (const part of parts) {
      currentPath += `${part}/`;
      const existing = nodeMap.get(currentPath);
      if (existing) {
        currentNode = existing;
      } else {
        currentNode = {
          name: part,
          path: currentPath,
          isFolder: true,
          children: [],
        };
        nodeMap.set(currentPath, currentNode);
        parent.push(currentNode);
      }
      currentNode.children = currentNode.children ?? [];
      parent = currentNode.children;
    }

    return currentNode;
  };

  const addFileNode = (filePath: string, size?: number) => {
    if (nodeMap.has(filePath)) {
      return;
    }
    const trimmed = filePath.replace(/\/$/, '');
    const parts = trimmed.split('/').filter(Boolean);
    const fileName = parts.pop();
    if (!fileName) {
      return;
    }
    const parentPath = parts.length ? `${parts.join('/')}/` : '';
    const parentNode = parentPath ? ensureFolder(parentPath) : null;
    const fileNode: TreeNode = {
      name: fileName,
      path: filePath,
      isFolder: false,
      size,
    };
    nodeMap.set(filePath, fileNode);
    if (parentNode) {
      parentNode.children = parentNode.children ?? [];
      parentNode.children.push(fileNode);
    } else {
      root.push(fileNode);
    }
  };

  for (const item of items) {
    if (item.type === 'directory') {
      ensureFolder(item.path);
    } else {
      addFileNode(item.path, item.size);
    }
  }

  sortTreeNodes(root);

  return root;
};

export function FileTree({ bucketId, onFileSelect }: FileTreeProps) {
  const { showToast } = useToast();
  const [tree, setTree] = useState<TreeNode[]>([]);
  const [treeItems, setTreeItems] = useState<R2BucketTreeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [creatingItem, setCreatingItem] = useState<'file' | 'folder' | null>(null);
  const [isScreenshotLoading, setIsScreenshotLoading] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [lastSelectedFile, setLastSelectedFile] = useState<string | null>(null);
  const [selectionAnchor, setSelectionAnchor] = useState<string | null>(null);
  const [renamingFile, setRenamingFile] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    targetPath: string;
  } | null>(null);
  const [draggingFiles, setDraggingFiles] = useState<string[]>([]);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  const [transferTargets, setTransferTargets] = useState<string[]>([]);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferBuckets, setTransferBuckets] = useState<string[]>([]);
  const [selectedTransferBucket, setSelectedTransferBucket] = useState('');
  const [transferBucketFilter, setTransferBucketFilter] = useState('');
  const [isTransferLoading, setIsTransferLoading] = useState(false);
  const [moveTargets, setMoveTargets] = useState<string[]>([]);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [selectedMoveDestination, setSelectedMoveDestination] = useState('/');
  const [moveDestinationFilter, setMoveDestinationFilter] = useState('');
  const [isMoveLoading, setIsMoveLoading] = useState(false);
  const [isCopyAllModalOpen, setIsCopyAllModalOpen] = useState(false);
  const [copyAllContent, setCopyAllContent] = useState('');
  const [copyAllErrors, setCopyAllErrors] = useState<Array<{ key: string; error: string }>>(
    []
  );
  const [isCopyAllLoading, setIsCopyAllLoading] = useState(false);

  const directoryOptions = useMemo(() => {
    const directories = treeItems
      .filter((item) => item.type === 'directory')
      .map((item) => normalizeDirectoryPath(item.path));
    const uniqueDirectories = Array.from(new Set(directories)).sort((a, b) =>
      a.localeCompare(b)
    );
    return ['/', ...uniqueDirectories];
  }, [treeItems]);

  useEffect(() => {
    loadTree();
  }, [bucketId]);

  useEffect(() => {
    const loadBuckets = async () => {
      try {
        const buckets = await r2BucketList();
        setTransferBuckets(buckets);
        if (buckets.length > 0) {
          setSelectedTransferBucket((current) => current || buckets[0]);
        }
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : 'Failed to load buckets',
          'error'
        );
      }
    };
    if (isTransferModalOpen && transferBuckets.length === 0) {
      void loadBuckets();
      return;
    }
    if (isTransferModalOpen && transferBuckets.length > 0) {
      setSelectedTransferBucket((current) => current || transferBuckets[0]);
    }
  }, [isTransferModalOpen, showToast, transferBuckets]);

  useEffect(() => {
    if (isMoveModalOpen && treeItems.length === 0 && !loading) {
      void loadTree();
    }
  }, [isMoveModalOpen, loading, treeItems.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F2' && selectedFiles.size === 1) {
        e.preventDefault();
        const filePath = Array.from(selectedFiles)[0];
        startRename(filePath);
      }

      if (e.key === 'Delete' && selectedFiles.size > 0) {
        e.preventDefault();
        handleBatchDelete(Array.from(selectedFiles));
      }

      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const visibleNodes = getVisibleNodes(tree);
        const currentFocus =
          lastSelectedFile ?? selectionAnchor ?? Array.from(selectedFiles)[0];
        if (!currentFocus) {
          return;
        }
        const anchor = selectionAnchor ?? currentFocus;
        const currentIndex = visibleNodes.findIndex((item) => item.path === currentFocus);
        if (currentIndex === -1) {
          return;
        }
        const nextIndex =
          e.key === 'ArrowUp'
            ? Math.max(0, currentIndex - 1)
            : Math.min(visibleNodes.length - 1, currentIndex + 1);
        const nextNode = visibleNodes[nextIndex];
        if (!nextNode) {
          return;
        }
        const anchorIndex = visibleNodes.findIndex((item) => item.path === anchor);
        if (anchorIndex === -1) {
          return;
        }
        const range = visibleNodes.slice(
          Math.min(anchorIndex, nextIndex),
          Math.max(anchorIndex, nextIndex) + 1
        );
        setSelectedFiles(new Set(range.map((item) => item.path)));
        setLastSelectedFile(nextNode.path);
        setSelectionAnchor(anchor);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lastSelectedFile, selectedFiles, selectionAnchor, tree]);

  const loadTree = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await r2BucketTree({ bucket_id: bucketId });
      setTreeItems(data.tree);
      const rootTree = buildTreeFromFlatList(data.tree);
      setTree(rootTree);
      expandAllFolders(rootTree);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const createFile = async () => {
    const name = prompt('New file name');
    if (!name) {
      return;
    }
    const lastSlashIndex = name.lastIndexOf('/');
    const directory_name =
      lastSlashIndex >= 0 ? name.substring(0, lastSlashIndex + 1) : '';
    const file_name = lastSlashIndex >= 0 ? name.substring(lastSlashIndex + 1) : name;
    try {
      setCreatingItem('file');
      setError(null);
      await r2FileCreate({
        bucket_id: bucketId,
        directory_name,
        file_name,
        content: '',
      });
      await loadTree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create file');
    } finally {
      setCreatingItem(null);
    }
  };

  const createFolder = async () => {
    const name = prompt('New folder name');
    if (!name) {
      return;
    }
    const directory_name = name.endsWith('/') ? name : `${name}/`;
    try {
      setCreatingItem('folder');
      setError(null);
      await r2DirectoryCreate({ bucket_id: bucketId, directory_name });
      await loadTree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setCreatingItem(null);
    }
  };

  const expandAllFolders = (nodes: TreeNode[]) => {
    const newExpanded = new Set<string>();
    const walk = (items: TreeNode[]) => {
      for (const node of items) {
        if (node.isFolder) {
          newExpanded.add(node.path);
          if (node.children) {
            walk(node.children);
          }
        }
      }
    };
    walk(nodes);
    setExpandedFolders(newExpanded);
  };

  const toggleFolder = (node: TreeNode) => {
    const newExpanded = new Set(expandedFolders);
    if (expandedFolders.has(node.path)) {
      newExpanded.delete(node.path);
    } else {
      newExpanded.add(node.path);
    }
    setExpandedFolders(newExpanded);
  };

  const getVisibleNodes = (nodes: TreeNode[]): TreeNode[] => {
    const result: TreeNode[] = [];
    for (const node of nodes) {
      result.push(node);
      if (node.isFolder && expandedFolders.has(node.path) && node.children) {
        result.push(...getVisibleNodes(node.children));
      }
    }
    return result;
  };

  const findNodeByPath = (nodes: TreeNode[], path: string): TreeNode | null => {
    for (const node of nodes) {
      if (node.path === path) {
        return node;
      }
      if (node.children) {
        const child = findNodeByPath(node.children, path);
        if (child) {
          return child;
        }
      }
    }
    return null;
  };

  const handleItemClick = (e: MouseEvent<HTMLDivElement>, node: TreeNode) => {
    const isModifier = e.ctrlKey || e.metaKey;
    if (e.shiftKey && (selectionAnchor || lastSelectedFile)) {
      const anchor = selectionAnchor ?? lastSelectedFile ?? node.path;
      const visibleNodes = getVisibleNodes(tree);
      const startIndex = visibleNodes.findIndex((item) => item.path === anchor);
      const endIndex = visibleNodes.findIndex((item) => item.path === node.path);
      if (startIndex !== -1 && endIndex !== -1) {
        const range = visibleNodes.slice(
          Math.min(startIndex, endIndex),
          Math.max(startIndex, endIndex) + 1
        );
        setSelectedFiles(new Set(range.map((item) => item.path)));
      } else {
        setSelectedFiles(new Set([node.path]));
      }
      setLastSelectedFile(node.path);
      setSelectionAnchor(anchor);
    } else if (isModifier) {
      const nextSelected = new Set(selectedFiles);
      if (nextSelected.has(node.path)) {
        nextSelected.delete(node.path);
      } else {
        nextSelected.add(node.path);
      }
      setSelectedFiles(nextSelected);
      setLastSelectedFile(node.path);
      setSelectionAnchor(node.path);
    } else {
      setSelectedFiles(new Set([node.path]));
      setLastSelectedFile(node.path);
      setSelectionAnchor(node.path);
    }

    if (node.isFolder) {
      toggleFolder(node);
    } else if (!e.shiftKey && !isModifier) {
      onFileSelect(node.path);
    }
    setContextMenu(null);
  };

  const handleContextMenu = (e: MouseEvent<HTMLDivElement>, node: TreeNode) => {
    e.preventDefault();
    if (!selectedFiles.has(node.path)) {
      setSelectedFiles(new Set([node.path]));
      setLastSelectedFile(node.path);
      setSelectionAnchor(node.path);
    }
    setContextMenu({ x: e.clientX, y: e.clientY, targetPath: node.path });
  };

  const startRename = (filePath: string) => {
    const fileName = filePath.split('/').filter(Boolean).pop() || '';
    setRenamingFile(filePath);
    setRenameValue(fileName);

    setTimeout(() => {
      const input = document.querySelector(
        'input[type="text"][data-rename="true"]'
      ) as HTMLInputElement | null;
      if (input) {
        const dotIndex = fileName.lastIndexOf('.');
        if (dotIndex > 0) {
          input.setSelectionRange(0, dotIndex);
        } else {
          input.select();
        }
      }
    }, 0);
  };

  const cancelRename = () => {
    setRenamingFile(null);
    setRenameValue('');
  };

  const executeRename = async () => {
    if (!renamingFile || !renameValue.trim()) {
      cancelRename();
      return;
    }

    const oldPath = renamingFile;
    const isFolder = oldPath.endsWith('/');
    const trimmedPath = isFolder ? oldPath.slice(0, -1) : oldPath;
    const parts = trimmedPath.split('/');
    parts[parts.length - 1] = renameValue.trim();
    const newPath = `${parts.join('/')}${isFolder ? '/' : ''}`;

    if (newPath === oldPath) {
      cancelRename();
      return;
    }

    try {
      await r2FileMove({
        bucket_id: bucketId,
        from_key: oldPath,
        to_key: newPath,
      });
      await loadTree();
      showToast('ファイル名を変更しました');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename');
      showToast('ファイル名の変更に失敗しました', 'error');
    } finally {
      setRenamingFile(null);
      setRenameValue('');
    }
  };

  const handleRenameKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeRename();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    }
  };

  const handleBatchDelete = async (filePaths: string[]) => {
    const confirmed = window.confirm(
      `本当に ${filePaths.length} 個のアイテムを削除しますか？`
    );

    if (!confirmed) return;

    try {
      setError(null);
      await Promise.all(
        filePaths.map((path) => r2FileDelete({ bucket_id: bucketId, key: path }))
      );
      setSelectedFiles(new Set());
      setLastSelectedFile(null);
      setSelectionAnchor(null);
      await loadTree();
      showToast(`${filePaths.length}個のアイテムを削除しました`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
      showToast('削除に失敗しました', 'error');
    }
  };

  const copyReferenceLink = async (filePath: string) => {
    try {
      const { url } = await r2ReferenceResolve({
        bucket_id: bucketId,
        key: filePath,
      });
      await navigator.clipboard.writeText(url);
      showToast('リンクをクリップボードにコピーしました');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy link');
      showToast('リンクのコピーに失敗しました', 'error');
    }
  };

  const handleDragStart = (e: DragEvent<HTMLDivElement>, filePath: string) => {
    const node = findNodeByPath(tree, filePath);
    if (node?.isFolder) {
      return;
    }
    const nextSelection = selectedFiles.has(filePath)
      ? Array.from(selectedFiles)
      : [filePath];
    if (!selectedFiles.has(filePath)) {
      setSelectedFiles(new Set([filePath]));
      setLastSelectedFile(filePath);
      setSelectionAnchor(filePath);
    }
    const fileSelection = nextSelection.filter((path) => {
      const selectedNode = findNodeByPath(tree, path);
      return selectedNode ? !selectedNode.isFolder : isFilePath(path);
    });
    if (fileSelection.length === 0) {
      return;
    }
    setDraggingFiles(fileSelection);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', fileSelection.join('\n'));
  };

  const handleDragEnd = () => {
    setDraggingFiles([]);
    setDropTarget(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, node: TreeNode) => {
    if (!node.isFolder) {
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTarget(node.path);
  };

  const moveFiles = async (filePaths: string[], destinationDir: string) => {
    if (filePaths.length === 0) {
      return;
    }
    const normalizedDestination = destinationDir
      ? normalizeDirectoryPath(destinationDir)
      : '';
    const failures: Array<{ path: string; reason: string }> = [];
    const moveCandidates: string[] = [];
    for (const filePath of filePaths) {
      const node = findNodeByPath(tree, filePath);
      const isFile = node ? !node.isFolder : isFilePath(filePath);
      if (!isFile) {
        failures.push({ path: filePath, reason: 'Directories cannot be moved.' });
        continue;
      }
      const currentParent = getParentDirectory(filePath);
      if (currentParent === normalizedDestination) {
        continue;
      }
      moveCandidates.push(filePath);
    }

    if (moveCandidates.length === 0) {
      if (failures.length > 0) {
        showToast(
          `Moved 0 items. Failed ${failures.length}.`,
          'error'
        );
      }
      return;
    }

    const reservedNames = getDirectChildFileNames(treeItems, normalizedDestination);
    let successCount = 0;
    for (const filePath of moveCandidates) {
      const baseName = getBaseName(filePath);
      if (!baseName) {
        continue;
      }
      let targetName = baseName;
      if (reservedNames.has(baseName)) {
        let suffixIndex = 1;
        while (reservedNames.has(buildUniqueName(baseName, suffixIndex))) {
          suffixIndex += 1;
        }
        targetName = buildUniqueName(baseName, suffixIndex);
      }
      reservedNames.add(targetName);
      const newPath = `${normalizedDestination}${targetName}`;
      try {
        await r2FileMove({
          bucket_id: bucketId,
          from_key: filePath,
          to_key: newPath,
        });
        successCount += 1;
      } catch (err) {
        const reason = err instanceof Error ? err.message : 'Failed to move';
        failures.push({ path: filePath, reason });
        showToast(`Failed to move ${filePath}: ${reason}`, 'error');
      }
    }
    await loadTree();
    if (successCount > 0 && failures.length === 0) {
      showToast(`Moved ${successCount} item${successCount === 1 ? '' : 's'}.`);
    } else {
      showToast(
        `Moved ${successCount} item${successCount === 1 ? '' : 's'}. Failed ${failures.length}.`,
        'error'
      );
    }
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>, targetPath: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggingFiles.length === 0) return;

    const targetNode = findNodeByPath(tree, targetPath);
    if (!targetNode?.isFolder) {
      setDraggingFiles([]);
      setDropTarget(null);
      return;
    }
    const destinationDir = targetPath;
    try {
      await moveFiles(draggingFiles, destinationDir);
    } finally {
      setDraggingFiles([]);
      setDropTarget(null);
    }
  };

  const handleDropToRoot = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (draggingFiles.length === 0) return;
    try {
      await moveFiles(draggingFiles, '');
    } finally {
      setDraggingFiles([]);
      setDropTarget(null);
    }
  };

  const openMoveModal = (filePaths: string[]) => {
    const movableFiles = filePaths.filter((path) => {
      const node = findNodeByPath(tree, path);
      return node ? !node.isFolder : isFilePath(path);
    });
    if (movableFiles.length === 0) {
      showToast('Directories cannot be moved.', 'error');
      return;
    }
    setMoveTargets(movableFiles);
    setSelectedMoveDestination('/');
    setMoveDestinationFilter('');
    setIsMoveModalOpen(true);
  };

  const handleMove = async () => {
    if (!directoryOptions.includes(selectedMoveDestination)) {
      showToast('Select a destination directory from the list.', 'error');
      return;
    }
    const destinationDir =
      selectedMoveDestination === '/' ? '' : normalizeDirectoryPath(selectedMoveDestination);
    setIsMoveLoading(true);
    try {
      await moveFiles(moveTargets, destinationDir);
      setSelectedFiles(new Set());
      setLastSelectedFile(null);
      setSelectionAnchor(null);
      setIsMoveModalOpen(false);
      setMoveTargets([]);
    } finally {
      setIsMoveLoading(false);
    }
  };

  const openTransferModal = (filePaths: string[]) => {
    setTransferTargets(filePaths);
    setTransferBucketFilter('');
    setIsTransferModalOpen(true);
  };

  const handleTransfer = async () => {
    if (!selectedTransferBucket) {
      showToast('Select a destination bucket.', 'error');
      return;
    }
    if (!transferBuckets.includes(selectedTransferBucket)) {
      showToast('Select a destination bucket from the list.', 'error');
      return;
    }
    setIsTransferLoading(true);
    try {
      if (transferTargets.length === 1) {
        await r2FileTransfer({
          from_bucket_id: bucketId,
          to_bucket_id: selectedTransferBucket,
          key: transferTargets[0],
        });
      } else {
        const response = await r2FileBulkTransfer({
          from_bucket_id: bucketId,
          to_bucket_id: selectedTransferBucket,
          keys: transferTargets,
        });
        if (response.errors && response.errors.length > 0) {
          response.errors.forEach((error) => {
            showToast(`Transfer failed for ${error.key}: ${error.error}`, 'error');
          });
          const successCount = transferTargets.length - response.errors.length;
          showToast(
            `Transferred ${successCount} item${successCount === 1 ? '' : 's'}. Failed ${response.errors.length}.`,
            'error'
          );
        } else {
          showToast(
            `Transferred ${transferTargets.length} item${transferTargets.length === 1 ? '' : 's'}.`
          );
        }
      }
      await loadTree();
      if (transferTargets.length === 1) {
        showToast('Transferred 1 item.');
      }
      setSelectedFiles(new Set());
      setLastSelectedFile(null);
      setSelectionAnchor(null);
      setIsTransferModalOpen(false);
      setTransferTargets([]);
      setSelectedTransferBucket('');
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Transfer failed';
      showToast(`Transfer failed: ${reason}`, 'error');
    } finally {
      setIsTransferLoading(false);
    }
  };

  const handleCopyAllJson = async () => {
    setIsCopyAllLoading(true);
    setCopyAllErrors([]);
    setCopyAllContent('');
    try {
      const response = await r2FileBulkContent({ bucket_id: bucketId });
      setCopyAllContent(response.content);
      setCopyAllErrors(response.errors ?? []);
      setIsCopyAllModalOpen(true);
      showToast('Loaded JSON content.');
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Failed to load JSON content';
      showToast(`Failed to load JSON content: ${reason}`, 'error');
    } finally {
      setIsCopyAllLoading(false);
    }
  };

  const handleCopyAllContent = async () => {
    try {
      await navigator.clipboard.writeText(copyAllContent);
      showToast('Copied JSON content to clipboard.');
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Copy failed';
      showToast(`Copy failed: ${reason}`, 'error');
    }
  };

  const handleCopyScreenshot = async () => {
    if (isScreenshotLoading) {
      return;
    }
    setIsScreenshotLoading(true);
    try {
      const rootElement = document.getElementById('root');
      if (!rootElement) {
        throw new Error('Root element not found');
      }
      const blob = await captureElementToPngBlob(rootElement);
      try {
        await copyBlobToClipboard(blob);
        showToast('Screenshot copied to clipboard');
      } catch {
        try {
          const dataUrl = await blobToDataUrl(blob);
          await navigator.clipboard.writeText(dataUrl);
          showToast('Failed to copy screenshot. Copied data URL instead.', 'error');
        } catch {
          downloadBlob(blob, 'screenshot.png');
          showToast('Failed to copy screenshot. Downloaded PNG instead.', 'error');
        }
      }
    } catch {
      showToast('Failed to copy screenshot', 'error');
    } finally {
      setIsScreenshotLoading(false);
    }
  };

  const handleScreenshotMouseDown = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const buildContextMenuItems = (): ContextMenuItem[] => {
    if (!contextMenu) {
      return [];
    }

    const targetNode = findNodeByPath(tree, contextMenu.targetPath);
    const isFolder = targetNode?.isFolder ?? false;

    if (selectedFiles.size > 1) {
      return [
        {
          label: 'Move...',
          onClick: () => {
            openMoveModal(Array.from(selectedFiles));
            setContextMenu(null);
          },
        },
        {
          label: 'Transfer (copy then delete source)...',
          onClick: () => {
            openTransferModal(Array.from(selectedFiles));
            setContextMenu(null);
          },
        },
        {
          label: 'Delete',
          onClick: () => {
            handleBatchDelete(Array.from(selectedFiles));
            setContextMenu(null);
          },
        },
        {
          label: 'Copy link',
          onClick: () => {},
          disabled: true,
        },
      ];
    }

    if (isFolder) {
      return [
        {
          label: 'Rename (F2)',
          onClick: () => {
            startRename(contextMenu.targetPath);
            setContextMenu(null);
          },
        },
        {
          label: 'Move...',
          onClick: () => {
            openMoveModal([contextMenu.targetPath]);
            setContextMenu(null);
          },
        },
        {
          label: 'Transfer (copy then delete source)...',
          onClick: () => {
            openTransferModal([contextMenu.targetPath]);
            setContextMenu(null);
          },
        },
        {
          label: 'Delete',
          onClick: () => {
            handleBatchDelete([contextMenu.targetPath]);
            setContextMenu(null);
          },
        },
      ];
    }

    return [
      {
        label: 'Rename (F2)',
        onClick: () => {
          startRename(contextMenu.targetPath);
          setContextMenu(null);
        },
      },
      {
      label: 'Move...',
      onClick: () => {
          openMoveModal([contextMenu.targetPath]);
          setContextMenu(null);
        },
      },
      {
        label: 'Transfer (copy then delete source)...',
        onClick: () => {
          openTransferModal([contextMenu.targetPath]);
          setContextMenu(null);
        },
      },
      {
        label: 'Delete',
        onClick: () => {
          handleBatchDelete([contextMenu.targetPath]);
          setContextMenu(null);
        },
      },
      { label: '', onClick: () => {}, separator: true },
      {
        label: 'Copy link',
        onClick: () => {
          copyReferenceLink(contextMenu.targetPath);
          setContextMenu(null);
        },
      },
    ];
  };

  const TreeItem = ({ node, depth = 0 }: { node: TreeNode; depth?: number }) => {
    const isExpanded = expandedFolders.has(node.path);
    const indent = depth * 16;
    const isSelected = selectedFiles.has(node.path);
    const isDropTarget = dropTarget === node.path;
    const backgroundColor = isDropTarget
      ? vscodeTheme.colors.dropBackground
      : isSelected
        ? vscodeTheme.colors.selectedBackground
        : 'transparent';

    return (
      <>
        <div
          draggable={!node.isFolder && renamingFile !== node.path}
          onDragStart={(e) => handleDragStart(e, node.path)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, node)}
          onDrop={(e) => handleDrop(e, node.path)}
          onClick={(e) => handleItemClick(e, node)}
          onContextMenu={(e) => handleContextMenu(e, node)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            paddingLeft: `${indent + 8}px`,
            cursor: 'pointer',
            color: vscodeTheme.colors.textPrimary,
            fontSize: vscodeTheme.typography.fontSize,
            fontFamily: vscodeTheme.typography.fontFamily,
            backgroundColor,
            transition: 'background-color 0.1s',
          }}
          onMouseEnter={(e) => {
            if (!isSelected && !isDropTarget) {
              e.currentTarget.style.backgroundColor =
                vscodeTheme.colors.hoverBackground;
            }
          }}
          onMouseLeave={(e) => {
            if (!isSelected && !isDropTarget) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
        >
          {node.isFolder && (
            <span style={{ marginRight: '4px', fontSize: '10px' }}>
              {isExpanded ? '▼' : '▶'}
            </span>
          )}
          <span style={{ marginRight: '6px' }}>{node.isFolder ? '📁' : '📄'}</span>
          {renamingFile === node.path ? (
            <input
              type="text"
              data-rename="true"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleRenameKeyDown}
              onBlur={cancelRename}
              autoFocus
              style={{
                flex: 1,
                background: vscodeTheme.colors.inputBackground,
                color: vscodeTheme.colors.textPrimary,
                border: `1px solid ${vscodeTheme.colors.focusBorder}`,
                outline: 'none',
                padding: '2px 4px',
                fontFamily: vscodeTheme.typography.fontFamily,
                fontSize: vscodeTheme.typography.fontSize,
              }}
            />
          ) : (
            <span style={{ flex: 1 }}>{node.name}</span>
          )}
          {!node.isFolder && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                copyReferenceLink(node.path);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: vscodeTheme.colors.textSecondary,
                fontSize: '12px',
              }}
              title="Copy link"
            >
              ⧉
            </button>
          )}
        </div>
        {node.isFolder && isExpanded && node.children && (
          <div>
            {node.children.map((child) => (
              <TreeItem key={child.path} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div>
        <div
          style={{
            padding: '8px 10px',
            borderBottom: `1px solid ${vscodeTheme.colors.border}`,
            backgroundColor: vscodeTheme.colors.headerBackground,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontSize: '11px',
              color: vscodeTheme.colors.textSecondary,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            EXPLORER
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              onClick={createFile}
              disabled={creatingItem !== null}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: creatingItem !== null ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="New file"
            >
              📄+
            </button>
            <button
              type="button"
              onClick={createFolder}
              disabled={creatingItem !== null}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: creatingItem !== null ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="New folder"
            >
              📁+
            </button>
            <button
              type="button"
              onClick={handleCopyAllJson}
              disabled={creatingItem !== null || isCopyAllLoading}
              style={{
                background: 'transparent',
                border: 'none',
                cursor:
                  creatingItem !== null || isCopyAllLoading ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="Copy all JSON"
            >
              {isCopyAllLoading ? '⏳' : '📋'}
            </button>
            <button
              type="button"
              onMouseDown={handleScreenshotMouseDown}
              onClick={(e) => {
                e.stopPropagation();
                void handleCopyScreenshot();
              }}
              disabled={isScreenshotLoading}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: isScreenshotLoading ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="Copy screenshot"
              aria-label="Copy screenshot"
            >
              {isScreenshotLoading ? '⏳' : '📸'}
            </button>
          </div>
        </div>
        <div style={{ padding: '10px', color: vscodeTheme.colors.textPrimary }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div
          style={{
            padding: '8px 10px',
            borderBottom: `1px solid ${vscodeTheme.colors.border}`,
            backgroundColor: vscodeTheme.colors.headerBackground,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span
            style={{
              fontSize: '11px',
              color: vscodeTheme.colors.textSecondary,
              fontFamily: vscodeTheme.typography.fontFamily,
            }}
          >
            EXPLORER
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              type="button"
              onClick={createFile}
              disabled={creatingItem !== null}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: creatingItem !== null ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="New file"
            >
              📄+
            </button>
            <button
              type="button"
              onClick={createFolder}
              disabled={creatingItem !== null}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: creatingItem !== null ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="New folder"
            >
              📁+
            </button>
            <button
              type="button"
              onClick={handleCopyAllJson}
              disabled={creatingItem !== null || isCopyAllLoading}
              style={{
                background: 'transparent',
                border: 'none',
                cursor:
                  creatingItem !== null || isCopyAllLoading ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="Copy all JSON"
            >
              {isCopyAllLoading ? '⏳' : '📋'}
            </button>
            <button
              type="button"
              onMouseDown={handleScreenshotMouseDown}
              onClick={(e) => {
                e.stopPropagation();
                void handleCopyScreenshot();
              }}
              disabled={isScreenshotLoading}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: isScreenshotLoading ? 'not-allowed' : 'pointer',
                color: vscodeTheme.colors.textPrimary,
                fontSize: '14px',
              }}
              title="Copy screenshot"
              aria-label="Copy screenshot"
            >
              {isScreenshotLoading ? '⏳' : '📸'}
            </button>
          </div>
        </div>
        <div style={{ padding: '10px', color: '#f48771' }}>Error: {error}</div>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        if (draggingFiles.length > 0) {
          e.preventDefault();
          setDropTarget(null);
        }
      }}
      onDrop={handleDropToRoot}
    >
      <div
        style={{
          padding: '8px 10px',
          borderBottom: `1px solid ${vscodeTheme.colors.border}`,
          backgroundColor: vscodeTheme.colors.headerBackground,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span
          style={{
            fontSize: '11px',
            color: vscodeTheme.colors.textSecondary,
            fontFamily: vscodeTheme.typography.fontFamily,
          }}
        >
          EXPLORER
        </span>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            onClick={createFile}
            disabled={creatingItem !== null}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: creatingItem !== null ? 'not-allowed' : 'pointer',
              color: vscodeTheme.colors.textPrimary,
              fontSize: '14px',
            }}
            title="New file"
          >
            📄+
          </button>
          <button
            type="button"
            onClick={createFolder}
            disabled={creatingItem !== null}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: creatingItem !== null ? 'not-allowed' : 'pointer',
              color: vscodeTheme.colors.textPrimary,
              fontSize: '14px',
            }}
            title="New folder"
          >
            📁+
          </button>
          <button
            type="button"
            onClick={handleCopyAllJson}
            disabled={creatingItem !== null || isCopyAllLoading}
            style={{
              background: 'transparent',
              border: 'none',
              cursor:
                creatingItem !== null || isCopyAllLoading ? 'not-allowed' : 'pointer',
              color: vscodeTheme.colors.textPrimary,
              fontSize: '14px',
            }}
            title="Copy all JSON"
          >
            {isCopyAllLoading ? '⏳' : '📋'}
          </button>
          <button
            type="button"
            onMouseDown={handleScreenshotMouseDown}
            onClick={(e) => {
              e.stopPropagation();
              void handleCopyScreenshot();
            }}
            disabled={isScreenshotLoading}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: isScreenshotLoading ? 'not-allowed' : 'pointer',
              color: vscodeTheme.colors.textPrimary,
              fontSize: '14px',
            }}
            title="Copy screenshot"
            aria-label="Copy screenshot"
          >
            {isScreenshotLoading ? '⏳' : '📸'}
          </button>
        </div>
      </div>
      {tree.map((node) => (
        <TreeItem key={node.path} node={node} />
      ))}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={buildContextMenuItems()}
          onClose={() => setContextMenu(null)}
        />
      )}
      {isMoveModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => {
            if (!isMoveLoading) {
              setIsMoveModalOpen(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: vscodeTheme.colors.editorBackground,
              border: `1px solid ${vscodeTheme.colors.border}`,
              borderRadius: '6px',
              padding: '16px',
              width: '320px',
              color: vscodeTheme.colors.textPrimary,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Move</h3>
            <p style={{ marginTop: 0, color: vscodeTheme.colors.textSecondary }}>
              Select a destination directory
            </p>
            <SearchableSelect
              label="Destination directory"
              items={directoryOptions.map((dir) => ({ id: dir, label: dir }))}
              selectedId={selectedMoveDestination}
              onChange={setSelectedMoveDestination}
              placeholder="/"
              filterText={moveDestinationFilter}
              onFilterTextChange={setMoveDestinationFilter}
              emptyText="No directories found"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setIsMoveModalOpen(false)}
                disabled={isMoveLoading}
                style={{
                  padding: '6px 10px',
                  background: 'transparent',
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  borderRadius: '4px',
                  cursor: isMoveLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMove}
                disabled={
                  isMoveLoading ||
                  !selectedMoveDestination ||
                  !directoryOptions.includes(selectedMoveDestination)
                }
                style={{
                  padding: '6px 10px',
                  backgroundColor: vscodeTheme.colors.buttonBackground,
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  borderRadius: '4px',
                  cursor: isMoveLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {isMoveLoading ? 'Moving...' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isTransferModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => {
            if (!isTransferLoading) {
              setIsTransferModalOpen(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: vscodeTheme.colors.editorBackground,
              border: `1px solid ${vscodeTheme.colors.border}`,
              borderRadius: '6px',
              padding: '16px',
              width: '320px',
              color: vscodeTheme.colors.textPrimary,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Transfer</h3>
            <p style={{ marginTop: 0, color: vscodeTheme.colors.textSecondary }}>
              Transfer (copy then delete source)
            </p>
            <SearchableSelect
              label="Destination bucket"
              items={transferBuckets.map((bucket) => ({
                id: bucket,
                label: bucket,
              }))}
              selectedId={selectedTransferBucket}
              onChange={setSelectedTransferBucket}
              placeholder="Select a bucket"
              filterText={transferBucketFilter}
              onFilterTextChange={setTransferBucketFilter}
              emptyText="No buckets found"
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setIsTransferModalOpen(false)}
                disabled={isTransferLoading}
                style={{
                  padding: '6px 10px',
                  background: 'transparent',
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  borderRadius: '4px',
                  cursor: isTransferLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleTransfer}
                disabled={
                  isTransferLoading ||
                  !selectedTransferBucket ||
                  !transferBuckets.includes(selectedTransferBucket)
                }
                style={{
                  padding: '6px 10px',
                  backgroundColor: vscodeTheme.colors.buttonBackground,
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  borderRadius: '4px',
                  cursor: isTransferLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {isTransferLoading ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}
      {isCopyAllModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={() => setIsCopyAllModalOpen(false)}
        >
          <div
            style={{
              backgroundColor: vscodeTheme.colors.editorBackground,
              border: `1px solid ${vscodeTheme.colors.border}`,
              borderRadius: '6px',
              padding: '16px',
              width: '420px',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              color: vscodeTheme.colors.textPrimary,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>All JSON Content</h3>
              <button
                type="button"
                onClick={() => setIsCopyAllModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: vscodeTheme.colors.textPrimary,
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <textarea
              readOnly
              value={copyAllContent}
              style={{
                flex: 1,
                minHeight: '200px',
                backgroundColor: vscodeTheme.colors.inputBackground,
                color: vscodeTheme.colors.textPrimary,
                border: `1px solid ${vscodeTheme.colors.border}`,
                borderRadius: '4px',
                padding: '8px',
                resize: 'vertical',
                fontFamily: 'monospace',
              }}
            />
            {copyAllErrors.length > 0 && (
              <div
                style={{
                  maxHeight: '120px',
                  overflowY: 'auto',
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  borderRadius: '4px',
                  padding: '8px',
                  fontSize: '12px',
                  color: vscodeTheme.colors.textSecondary,
                }}
              >
                <strong>Errors</strong>
                <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
                  {copyAllErrors.map((error) => (
                    <li key={error.key}>
                      {error.key}: {error.error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                type="button"
                onClick={handleCopyAllContent}
                style={{
                  padding: '6px 10px',
                  backgroundColor: vscodeTheme.colors.buttonBackground,
                  border: `1px solid ${vscodeTheme.colors.border}`,
                  color: vscodeTheme.colors.textPrimary,
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Copy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
